import { Node, calculateNodeHeight } from '../../modeling';
import { NODE_WIDTH } from '../../../shared/constants';
import { safeNum } from './canvasUtils';

/**
 * Dimensions helper
 */
const getRect = (node: Node) => {
    const w = NODE_WIDTH;
    const h = node.computedHeight || calculateNodeHeight(node.name);
    return {
        x: safeNum(node.x),
        y: safeNum(node.y),
        w,
        h,
        cx: safeNum(node.x) + w / 2,
        cy: safeNum(node.y) + h / 2
    };
};

/**
 * Determines which visual side an edge should use between two nodes.
 * 
 * Logic:
 * 1. Calculate the center points of both nodes.
 * 2. Calculate the aspect ratio adjusted slope between them.
 * 3. Map the vector (Source -> Target) to one of the 4 cardinal directions (N, S, E, W).
 * 4. This ensures edges exit from the "face" most directly looking at the target.
 */
export function getLogicalSide(s: Node, t: Node): { s: 'N' | 'S' | 'E' | 'W'; t: 'N' | 'S' | 'E' | 'W' } {
    const sR = getRect(s);
    const tR = getRect(t);

    const dx = tR.cx - sR.cx;
    const dy = tR.cy - sR.cy;

    // Aspect Ratio Check for Source
    const sIsHorizontal = Math.abs(dx) * sR.h > Math.abs(dy) * sR.w;
    const sSide = sIsHorizontal ? (dx > 0 ? 'E' : 'W') : (dy > 0 ? 'S' : 'N');

    // For Target, the vector is reversed (Source relative to Target)
    const tdx = sR.cx - tR.cx;
    const tdy = sR.cy - tR.cy;
    const tIsHorizontal = Math.abs(tdx) * tR.h > Math.abs(tdy) * tR.w;
    const tSide = tIsHorizontal ? (tdx > 0 ? 'E' : 'W') : (tdy > 0 ? 'S' : 'N');

    return { s: sSide, t: tSide };
}

/**
 * Calculates a point on the node boundary for a specific side and port offset.
 * 
 * Logic:
 * 1. Distributes ports evenly along the specified side.
 * 2. If only one link exists, it uses the center of the side.
 * 3. Uses a (tot + 1) step calculation to avoid links hitting the corners.
 */
function getPortPoint(node: Node, side: 'N' | 'S' | 'E' | 'W', idx: number, tot: number) {
    const r = getRect(node);

    // Distribute ports evenly along the side
    const distribution = (size: number) => {
        if (tot <= 1) return size / 2;
        const step = size / (tot + 1);
        return step * (idx + 1);
    };

    switch (side) {
        case 'N': return { x: r.x + distribution(r.w), y: r.y };
        case 'S': return { x: r.x + distribution(r.w), y: r.y + r.h };
        case 'E': return { x: r.x + r.w, y: r.y + distribution(r.h) };
        case 'W': return { x: r.x, y: r.y + distribution(r.h) };
    }
}

/**
 * Apply padding to the end of the route to ensure arrowhead doesn't overlap node
 */
function applyArrowPadding(route: number[]): number[] {
    if (!route || route.length < 4) return route;

    // Small back-off to ensure arrow tip is visible and not clipped by node border
    const ARROW_PADDING = 0;
    const last = route.length - 1;

    const newRoute = [...route];
    const lx = newRoute[last - 1];
    const ly = newRoute[last];
    const px = newRoute[last - 3];
    const py = newRoute[last - 2];

    const dx = lx - px;
    const dy = ly - py;
    const length = Math.sqrt(dx * dx + dy * dy);

    if (length > ARROW_PADDING) {
        const ratio = ARROW_PADDING / length;
        newRoute[last - 1] = lx - dx * ratio;
        newRoute[last] = ly - dy * ratio;
    }
    return newRoute;
}

/**
 * Generates an orthogonal (manhattan) path between two nodes within the same context.
 * 
 * Logic:
 * 1. Determines optimal exit/entry sides.
 * 2. Extends a "stub" (minSeg) out from each node to create clear separation.
 * 3. If sides are opposite (e.g. East -> West), creates a Z-shaped "dogleg" through the midpoint.
 * 4. Otherwise, creates a simple 90-degree corner.
 */
export function calculateDynamicPoints(
    source: Node,
    target: Node,
    sIdx = 0, sTot = 1,
    tIdx = 0, tTot = 1
): number[] {
    const { s: sideS, t: sideT } = getLogicalSide(source, target);

    const pStart = getPortPoint(source, sideS, sIdx, sTot);
    const pEnd = getPortPoint(target, sideT, tIdx, tTot);

    let minSeg = 20; // Default MIN_SEG

    const dirs = {
        'N': { x: 0, y: -1 }, 'S': { x: 0, y: 1 },
        'E': { x: 1, y: 0 }, 'W': { x: -1, y: 0 }
    };

    const dS = dirs[sideS];
    const dT = dirs[sideT]; // Normal vector of target face (Outwards)

    // Check availability space for "Opposite" connections
    const isOpposite = (sideS === 'N' && sideT === 'S') || (sideS === 'S' && sideT === 'N') ||
        (sideS === 'E' && sideT === 'W') || (sideS === 'W' && sideT === 'E');

    if (isOpposite) {
        let dist = 0;
        if (Math.abs(dS.x) > 0) { // Horizontal
            // projected distance: (Target - Source) * direction
            dist = (pEnd.x - pStart.x) * dS.x;
        } else { // Vertical
            dist = (pEnd.y - pStart.y) * dS.y;
        }

        if (dist > 0 && dist < 2 * minSeg) {
            minSeg = dist / 2;
        }
    }

    // Move out from nodes
    const p1 = { x: pStart.x + dS.x * minSeg, y: pStart.y + dS.y * minSeg };
    const pLast = { x: pEnd.x + dT.x * minSeg, y: pEnd.y + dT.y * minSeg };

    const points: { x: number, y: number }[] = [pStart, p1];

    // Determine path based on sides
    // Logic: greedy orthogonal routing

    // Case 1: Simple Face-to-Face (e.g. Left -> Right)
    // If sides are opposite and aligned
    if (isOpposite) {
        // Find Mid
        if (Math.abs(dS.x) > 0) { // Horizontal
            const midX = p1.x; // Always bend exactly minSeg (20px) from the source node
            points.push({ x: midX, y: p1.y });
            points.push({ x: midX, y: pLast.y });
        } else { // Vertical
            const midY = p1.y; // Always bend exactly minSeg (20px) from the source node 
            points.push({ x: p1.x, y: midY });
            points.push({ x: pLast.x, y: midY });
        }
    } else {
        // Standard Dogleg
        // Go Horz then Vert or vice versa

        // If Leaving Horizontal
        if (Math.abs(dS.x) > 0) {
            // Try to go to Target X immediately?
            // Path: p1 -> (pLast.x, p1.y) -> pLast
            points.push({ x: pLast.x, y: p1.y });
        } else {
            // Leaving Vertical
            // Path: p1 -> (p1.x, pLast.y) -> pLast
            points.push({ x: p1.x, y: pLast.y });
        }
    }

    points.push(pLast);
    points.push(pEnd);

    // Filter duplicates and flatten
    const result: number[] = [];
    result.push(points[0].x, points[0].y);

    for (let i = 1; i < points.length; i++) {
        const prev = points[i - 1];
        const curr = points[i];
        if (Math.abs(prev.x - curr.x) < 0.5 && Math.abs(prev.y - curr.y) < 0.5) continue;
        result.push(curr.x, curr.y);
    }

    return applyArrowPadding(result);
}

/**
 * Implements "Gutter" (Lane) Routing for links that cross Slice boundaries.
 * 
 * This is a specialized routing pattern designed to keep inter-slice connections clean:
 * 
 * 1. **Exit Directionality**: 
 *    - Domain Events: Exit from the BOTTOM (South) to represent "Eventual Consistency".
 *    - Others: Exit East (Forward) or West (Feedback).
 * 2. **Entry Directionality**: 
 *    - Arrows always enter the target from the Left (West) for forward links, or Right (East) for feedback.
 * 3. **The Lane**: 
 *    - Instead of a random zig-zag, the path drops into a fixed "Lane" or gutter 
 *      between the slices (e.g., 60px before the target slice).
 *    - All vertical travel happens in this lane.
 *    - This creates a structured "Bus" look for cross-slice communication.
 */
export function calculateSmartPoints(
    sourceNode: Node,
    targetNode: Node,
    sliceBounds: Map<string, { minX: number; maxX: number; minY: number; maxY: number }>
): number[] | null {
    const sSliceId = sourceNode.sliceId || '__default_slice__';
    const tSliceId = targetNode.sliceId || '__default_slice__';

    // Only apply smart routing for inter-slice edges
    if (sSliceId === tSliceId) return null;

    const tBounds = sliceBounds.get(tSliceId);
    if (!tBounds) return null;

    const sourceRect = getRect(sourceNode);


    // Determine directionality
    // Standard: Left -> Right (Source is to the left of Target Slice)
    // Feedback: Right -> Left (Source is to the right of Target Slice)
    // We use the Slice bounds to be more robust than just node positions
    const isForward = sourceRect.cx < tBounds.minX;
    const isFeedback = sourceRect.cx > tBounds.maxX;

    // Default to dynamic if inside the slice horizontal range (vertical overlap)
    if (!isForward && !isFeedback) return null;

    // Calculate the "Gutter" or "Lane" X coordinate
    // 20px padding (Slice) + 20px requested gap = 40px
    const LANE_OFFSET = 60;

    let laneX = 0;

    if (isForward) {
        // Lane is to the LEFT of the target slice
        laneX = tBounds.minX - LANE_OFFSET;
    } else {
        // Lane is to the RIGHT of the target slice
        laneX = tBounds.maxX + LANE_OFFSET;
    }

    // Determine Start and End Points on the nodes
    // For this specific routing, we ideally want accurate port behavior
    // For simplicity/robustness, we'll force:
    // Forward: Source East -> Target West (via Lane) is not quite right because Lane is to the Left of Target... 
    // Wait, if Forward: Source -> [Lane] -> Target 
    // Lane is at Target.minX - 40. Source is to the left.
    // So Source -> Lane -> Target.
    // Source should exit East. Target should enter West.

    // Feedback: Source -> [Lane] -> Target
    // Lane is at Target.maxX + 40. Source is right.
    // Source should exit West. Target should enter East.

    // Check if Source is DOMAIN_EVENT
    // If so, force 'S' exit (Bottom).
    const isSourceDomainEvent = sourceNode.type === 'DOMAIN_EVENT';

    // Logic:
    // If DomainEvent: Exit South -> Down -> LaneX -> TargetY -> Target
    // If Other: Exit East/West -> LaneX -> TargetY -> Target

    const startSide = isSourceDomainEvent ? 'S' : (isForward ? 'E' : 'W');
    const endSide = isForward ? 'W' : 'E';

    // We use index 0 for now as we don't have the full graph index context passed to this function easily 

    const pStart = getPortPoint(sourceNode, startSide, 0, 1);
    const pEnd = getPortPoint(targetNode, endSide, 0, 1);

    const points: number[] = [];
    points.push(pStart.x, pStart.y);

    if (isSourceDomainEvent) {
        // 1. Go Down 20px from Bottom Center
        const verticalStub = 20;
        points.push(pStart.x, pStart.y + verticalStub);

        // 2. Go Horizontal to LaneX
        points.push(laneX, pStart.y + verticalStub);

        // 3. Go Vertical to Target Y
        points.push(laneX, pEnd.y);

        // 4. Go Horizontal to Target
        points.push(pEnd.x, pEnd.y);
    } else {
        // Standard "Gutter" Routing
        // P0: Start
        // P1: (LaneX, StartY)
        // P2: (LaneX, EndY)
        // P3: End

        points.push(laneX, pStart.y);
        points.push(laneX, pEnd.y);
        points.push(pEnd.x, pEnd.y);
    }

    return applyArrowPadding(points);
}

export function resolveLinkPoints(
    sourceNode: Node,
    targetNode: Node,
    _cachedRoute?: number[],
    _sIdx = 0, _sTot = 1,
    _tIdx = 0, _tTot = 1
): number[] {
    // Determine logical vertical flow (matches worker's ranks)
    const TYPE_RANKS: Record<string, number> = {
        'SCREEN': 0, 'AUTOMATION': 1, 'COMMAND': 2, 'READ_MODEL': 2, 'DOMAIN_EVENT': 3, 'INTEGRATION_EVENT': 4
    };
    const sRank = TYPE_RANKS[sourceNode.type] ?? 0;
    const tRank = TYPE_RANKS[targetNode.type] ?? 0;
    const sideS = sRank <= tRank ? 'S' : 'N';
    const sideT = sRank < tRank ? 'N' : 'S';

    // TWIN-CABLE BUNDLING:
    // To prevent overlaps when a node has both incoming and outgoing traffic on the same face,
    // we use two parallel bundled "cables" offset from the center.
    const BUNDLE_OFFSET = 20; // 20px Grid Alignment
    
    // Use dynamic widths for exact center alignment
    const sW = (sourceNode as any).width || (sourceNode as any).computedWidth || NODE_WIDTH;
    const tW = (targetNode as any).width || (targetNode as any).computedWidth || NODE_WIDTH;
    const sCenterX = (sourceNode.x || 0) + sW / 2;
    const tCenterX = (targetNode.x || 0) + tW / 2;

    const pStart = getPortPoint(sourceNode, sideS, 0, 1);
    const pEnd = getPortPoint(targetNode, sideT, 0, 1);

    // SEMANTIC LANE SEPARATION:
    // To prevent overlaps at the node face (especially on Screen elements):
    // Write-Path (COMMANDs/triggers) -> Right Lane (+20)
    // Read-Path (READ_MODELS/renders) -> Left Lane (-20)
    const isWritePath = sourceNode.type === 'COMMAND' || targetNode.type === 'COMMAND' || targetNode.type === 'DOMAIN_EVENT';
    const laneOffset = isWritePath ? BUNDLE_OFFSET : -BUNDLE_OFFSET;

    if (Math.abs(sCenterX - tCenterX) < 5) {
        // Aligned Vertical Column
        pStart.x = sCenterX + laneOffset;
        pEnd.x = tCenterX + laneOffset;
    } else {
        // Branching Manhattan
        pStart.x = sCenterX + laneOffset;
        pEnd.x = tCenterX + laneOffset;
    }

    // RESTORE MANHATTAN STUB: Strictly 20px as requested
    const STUB = 20;
    const sDir = sideS === 'S' ? 1 : -1;
    const tDir = sideT === 'S' ? 1 : -1;

    // IF ALIGNED: If the inbound and outbound cables happen to align vertically
    if (Math.abs(pStart.x - pEnd.x) < 2) {
        return applyArrowPadding([pStart.x, pStart.y, pEnd.x, pEnd.y]);
    }

    // "CABLE & BRANCH" ROUTING:
    const branchY = pStart.y + (sDir * STUB);
    const entryY = pEnd.y + (tDir * STUB);
    
    // Check if we have a worker route to potentially reuse architectural "midY" 
    // but the user strictly prefers branching at 20px from the node.
    
    return applyArrowPadding([
        pStart.x, pStart.y,           // Start (Bundled)
        pStart.x, branchY,            // vertical stem (20px)
        pEnd.x, branchY,              // Horizontal branch (Happens AT 20px)
        pEnd.x, entryY,               // Vertical drop/rise to target stub
        pEnd.x, pEnd.y                // Into target
    ]);
}





export function resolvePortPoint(node: Node, targetX: number, targetY: number): { x: number, y: number } {
    const r = getRect(node);
    const dx = targetX - r.cx;
    const dy = targetY - r.cy;
    const isHorizontal = Math.abs(dx) * r.h > Math.abs(dy) * r.w;

    if (isHorizontal) {
        return {
            x: dx > 0 ? r.x + r.w : r.x,
            y: Math.max(r.y, Math.min(r.y + r.h, targetY))
        };
    } else {
        return {
            x: Math.max(r.x, Math.min(r.x + r.w, targetX)),
            y: dy > 0 ? r.y + r.h : r.y
        };
    }
}
